import React, { useState, useEffect } from 'react'
import { fetchStartups } from '../../../networking/network'
import Startup, { CommercialViabilityScore } from '../../../models/startup'
import { 
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'

import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useAppSelector } from '../../../store/hooks'
import { Add, Search } from '@mui/icons-material'

interface Props {

}

const StartupList = (props: Props) => {
  const [startups, setStartups] = useState<Startup[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [rowsPerPage, setRowsPerPage] = React.useState(25)
  const [page, setPage] = useState(1)
  const [totalRowsCount, setTotalRowsCount] = useState(0)

  let searchText = ''

  const accessToken = useAppSelector(state => state.user.token)

  const navigation = useNavigate()

  const [submitSearchText, setSubmitSearchText] = useState<string | undefined>(undefined)

  const loadStartups = async (pageNumber: number, searchText?: string) => {
    if (accessToken === null) {
      return
    }

    const pageNumberConvertedToZeroIndex = pageNumber - 1

    if (pageNumberConvertedToZeroIndex > 0 && pageNumberConvertedToZeroIndex * rowsPerPage >= totalRowsCount) {
      console.error('Unable to load that page.  Exceeds total count.')
      return
    }

    setIsLoading(true)

    const results = await fetchStartups(accessToken, rowsPerPage, rowsPerPage * pageNumberConvertedToZeroIndex, submitSearchText)

    const count: number = results.total_count

    setTotalRowsCount(count)

    setStartups(results.startups)

    setIsLoading(false)
  }

  useEffect(() => {
    loadStartups(page, submitSearchText)
  }, [page])

  useEffect(() => {
    loadStartups(page, submitSearchText)
  }, [rowsPerPage])

  useEffect(() => {
    loadStartups(1, submitSearchText)
  }, [submitSearchText]) 

  const handleChangeRowsPerPage = (
    value: number
  ) => {
    setRowsPerPage(value)
    setPage(1)
    setStartups([])
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      editable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<string>) => (
        <Link to={`/startups/${params.row.id}`}>{params.value}</Link>
      )
    },
    {
      field: 'commercialViabilityScore',
      headerName: 'Aggregate Score',
      editable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommercialViabilityScore | null | undefined>) => (
        <div>
          {params.value &&
            <Typography fontWeight='bold'>{params.value.score}</Typography>
          }
          {!params.value &&
            <Button variant='contained' color='primary' onClick={() => {
              navigation(`/startups/${params.row.id}/score`)
            }}>
              Score
            </Button>
          }
        </div>
      )
    },
    {
      field: 'companyWebsite',
      headerName: 'Website',
      editable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<string>) => (
        <a href={params.value} target='_blank'>{params.value}</a>
      )
    },
    {
      field: 'contactName',
      headerName: 'Contact',
      editable: false,
      flex: 1
    },
    {
      field: 'contactEmail',
      headerName: 'Email',
      editable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<string>) => (
        <a href={`mailto: ${params.value}`}>
          {params.value}
        </a>
      )
    }
  ]

  const startupRows: Startup[] = startups

  return (
    <React.Fragment>
    <Box sx={{
      padding: '16px',
      marginBottom: '58px'
    }}>
      <Box display='flex' flexDirection='row' gap='16px' >
        <form style={{
          flexGrow: 3
        }} onSubmit={(e) => {
          e.preventDefault()
          setSubmitSearchText(searchText)
        }}>
            <TextField
              size='small'
              variant='outlined'
              placeholder='Search...'
              onChange={(event) => {
                searchText = event.target.value
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                )
              }}
              sx={{
                marginBottom: '16px',
                backgroundColor: 'white',
                width: '100%'
              }} />
          </form>
          <FormControl sx={{
            flexGrow: 0.5
          }} size='small'>
            <Select
              labelId='items-per-page'
              id='items-per-page-picker'
              size='small'
              sx={{
                backgroundColor: 'white'
              }}
              value={rowsPerPage}
              onChange={(newCount: SelectChangeEvent<number>) => {
                handleChangeRowsPerPage(newCount.target.value as number)
              }}
            >
              <MenuItem value={25}>Show 25</MenuItem>
              <MenuItem value={50}>Show 50</MenuItem>
              <MenuItem value={100}>Show 100</MenuItem>
            </Select>
          </FormControl>
          <Button size='small' startIcon={<Add />} variant='contained' sx={{
            flexGrow: 0.3,
            maxHeight: '37px',
            minWidth: '150px'
          }} onClick={() => {
            navigation('/startups/add')
          }}>
            Add Startup
          </Button>
        </Box>
      <Paper>
        <div style={{
          width: '100%'
        }}>
          <DataGrid
              rows={startupRows || []}
              rowCount={startupRows.length}
              autoHeight
              columns={columns}
              pageSize={rowsPerPage}
              hideFooter={true}
              page={0}
              disableSelectionOnClick={true}
              disableColumnSelector={true}
              loading={isLoading}
              sx={{
                '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                  outline: 'none'
                }
              }}
          />
          </div>
      </Paper>
    </Box>
    <Box sx={{
      position: 'fixed',
      bottom: '0%',
      width: '100%',
      height: '58px',
      paddingLeft: '16px',
      backgroundColor: 'white',
      boxShadow: 5
    }} display='flex' flexDirection='row' alignItems='center'>
      <Pagination 
        count={Math.ceil(totalRowsCount / rowsPerPage)} 
        variant='outlined'
        shape='rounded'
        color='primary'
        onChange={(event, page: number) => {
          setPage(page)
        }}   
      />
    </Box>
    </React.Fragment>
  )
}

export default StartupList
